import { useState } from 'react'
import { RegisterFormInfoTypes } from '../helpers/enums'

export interface TRegisterFullInfo {
    registerInfo: TRegisterInfo
    personalInfo: TPersonalInfo
    professionalInfo: Array<TProfessionalInfo>
    educationalInfo: Array<TEducationalInfo>
}

export const DEFAULT_REGISTER_INFO: TRegisterInfo = {
    email: '',
    documentValue: '',
    documentType: '',
    password: '',
    repeatPassword: '',
    urlUserLinkedIn: '',
}

type DocumentType =
    | 'DNI'
    | 'Pasaporte'
    | 'Cédula de Identidad'
    | 'Libreta de Enrolamiento'
    | 'Libreta Cívica'
    | 'Otro'
    | ''

export interface TRegisterInfo extends TRegisterFormInfo {
    email: string
    documentValue: string
    documentType: DocumentType
    password: string
    repeatPassword: string
    urlUserLinkedIn?: string
}

export const DEFAULT_PERSONAL_INFO = {
    firstName: '',
    lastName: '',
    birthdate: '',
    phone: '',
    nationality: 'Argentina',
    livesAbroad: false,
    image: '',
    abilities: [],
}

export interface TPersonalInfo extends TRegisterFormInfo {
    firstName: string
    lastName: string
    birthdate: string
    phone: string
    nationality: string
    livesAbroad: boolean
    image: string
    abilities: string[]
}

export const DEFAULT_PROFESSIONAL_INFO: TProfessionalInfo = {
    id: '',
    companyName: '',
    job: '',
    startDate: '',
    endingDate: '',
    responsabilities: '',
    country: 'Argentina',
    abilities: [],
}

export interface TProfessionalInfo extends TRegisterFormInfo {
    id: string
    companyName?: string
    job?: string
    startDate?: string
    endingDate?: string
    responsabilities?: string
    country?: string
    abilities?: string[]
}

export const DEFAULT_EDUCATIONAL_INFO: TEducationalInfo = {
    id: '',
    degree: '',
    school: '',
    carrer: '',
    startDate: '',
    endingDate: '',
    country: 'Argentina',
    abilities: [],
}

export interface TEducationalInfo extends TRegisterFormInfo {
    id: string
    degree?: string
    school?: string
    carrer?: string
    startDate?: string
    endingDate?: string
    country?: string
    abilities?: string[]
}

export interface TRegisterFormInfo {}

const useFormRegister = () => {
    const [registerInfo, setRegisterInfo] = useState<TRegisterInfo>(DEFAULT_REGISTER_INFO)
    const [personalInfo, setPersonalInfo] = useState<TPersonalInfo>(DEFAULT_PERSONAL_INFO)
    const [professionalInfo, setProfessionalInfo] = useState<TProfessionalInfo[]>([])
    const [educationalInfo, setEducationalInfo] = useState<TEducationalInfo[]>([])

    const saveCarreerInfo = (
        type: RegisterFormInfoTypes.Professional | RegisterFormInfoTypes.Educational,
        newInfo: TEducationalInfo | TProfessionalInfo,
        id?: string,
    ) => {
        const setInfo =
            type === RegisterFormInfoTypes.Professional ? setProfessionalInfo : setEducationalInfo

        if (id) {
            setInfo((prev: TEducationalInfo[] | TProfessionalInfo[]) =>
                prev.map((info) => (info.id === id ? newInfo : info)),
            )
        } else {
            setInfo((prev: TEducationalInfo[] | TProfessionalInfo[]) => [
                ...prev,
                {
                    ...newInfo,
                    id: (prev.length + 1).toString(),
                },
            ])
        }
    }

    const saveInfo = (type: RegisterFormInfoTypes, info: TRegisterFormInfo, id?: string) => {
        switch (type) {
            case RegisterFormInfoTypes.Register:
                setRegisterInfo(info as TRegisterInfo)
                break
            case RegisterFormInfoTypes.Personal:
                setPersonalInfo(info as TPersonalInfo)
                break
            case RegisterFormInfoTypes.Professional:
                saveCarreerInfo(type, info as TProfessionalInfo, id)
                break
            case RegisterFormInfoTypes.Educational:
                saveCarreerInfo(type, info as TEducationalInfo, id)
                break
        }
    }

    const deleteInfo = (
        type: RegisterFormInfoTypes.Professional | RegisterFormInfoTypes.Educational,
        id: string,
    ) => {
        const [info, setInfo] =
            type === RegisterFormInfoTypes.Professional
                ? [professionalInfo, setProfessionalInfo]
                : [educationalInfo, setEducationalInfo]

        const updatedInfo = (info as Array<TEducationalInfo | TProfessionalInfo>)
            .filter((item) => item.id !== id)
            .map((item, index) => ({
                ...item,
                id: (index + 1).toString(),
            }))

        setInfo(updatedInfo)
    }

    return {
        registerInfo,
        personalInfo,
        professionalInfo,
        educationalInfo,
        saveInfo,
        deleteInfo,
    }
}

export default useFormRegister
